import './FairValueEvaluationPage.scss'

import IconCheck from "assets/img/icon-check.svg?react"
import IconHierarchy from "assets/img/icon-hierarchy-branch.svg?react"
import IconLimitComment from "assets/img/icon-limit-comment.svg?react"
import IconAutoLimit from "assets/img/icon-auto-limit.svg?react"
import IconPencil from "assets/img/icon-pencil.svg?react"
import noDocumentsImg from "assets/img/no-documents.svg"


import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import InfiniteScroll from "react-infinite-scroller"
import { useNavigate } from 'react-router-dom'
import { useUnit } from 'effector-react'
import cn from 'classnames'


import { Dayjs } from "utils/dayjs"
import { Flex, FlexItem } from "components/Flex"
import Page from "components/Page/Page"
import Header from "components/Header/Header"
import Toolbar from "components/Toolbar/Toolbar"
import { useFairValueEvaluation } from "./useFairValueEvaluation"
import { useFairValueEvaluationPeriods } from "./useFairValueEvaluationPeriods"
import BookingPeriodsSelect from "./BookingPeriodsSelect"
import { Grid, GridItem } from "components/Grid"
import Container from "components/Container/Container"
import Table, { TableColumn, TableRow } from "components/Table/Table"
import Text from "components/Text/Text"
import Lmid from "components/Lmid/Lmid"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import NoValue from "components/NoValue/NoValue"
import StatusLight, { StatusLightType } from "components/StatusLight/StatusLight"
import Button from "components/Button/Button"
import { debounce } from "lodash"
import { FairValueEvaluationItem, FairValueEvaluationSortBy, } from "models/FairValueEvaluation"
import SpoilerHeader from "components/Spoiler/SpoilerHeader"
import { Guid, IApiLink } from "types"
import useUserEmails from 'hooks/useUserEmails'
import Routes from 'routes/index'
import { usePermissions } from 'stores/permissions'
import { FilterCategory, FilterStorage, UserPermission, ViewSettingsStorage } from 'config/constants'
import Spinner from 'components/Spinner/Spinner'
import useFilter from 'components/Filter/hooks/useFilter'
import Filter from 'components/Filter/Filter'
import { $userProfitCenterIds } from 'stores/profitCenters'
import { collectFilterProps } from "utils/filters"
import Tag, { TagTheme } from 'components/Tag/Tag'
import toastMessages from 'stores/toastMessages'
import useBookValues from './useBookValues'
import Tooltip from 'components/Tooltip/Tooltip'
import AmountWithCurrency from 'models/AmountWithCurrency'
import EditEstimatedNetSalePriceModal from './EditEstimatedNetSalePriceModal'
import viewSettingsStore from "stores/viewSettings"
import ViewSettingsFairValueEvaluationModel from "models/ViewSettingsFairValueEvaluation"
import ViewSettingsFairValueEvaluation from 'components/ViewSettings/ViewSettingsFairValueEvaluation'

const resolveBookValue = ({ item, id, bookValues, EmptyCell, isTopLevel }: { item: FairValueEvaluationItem, id: Guid | null, bookValues: Record<Guid, AmountWithCurrency | null | undefined>, EmptyCell: ReactNode, isTopLevel: boolean }) => (
    <Text bold={isTopLevel} >
        {item.bookValue && item.bookValue.print()}
        {!item.bookValue && id && bookValues[id] && bookValues[id]?.print()}
        {
            !item.bookValue && id && bookValues[id] === undefined && <Spinner size={32} />
        }
        {
            (!item.bookValue && id && bookValues[id] === null || !id) && EmptyCell
        }
    </Text>)

const FairValueEvaluationPage = function () {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission } = usePermissions()

    const userProfitCenterIds = useUnit($userProfitCenterIds)
    const { viewSettings, setViewSettings } = viewSettingsStore.useViewSettings<ViewSettingsFairValueEvaluationModel>(ViewSettingsStorage.fairValueEvaluation)
    const { filters, addFilter } = useFilter({ storage: FilterStorage.fairValueEvaluation })
    const filtersData = useMemo(() => ({ ...collectFilterProps(filters), userProfitCenterIds: userProfitCenterIds ?? [], onlyReferenceNumbersWithMachines: viewSettings.onlyReferenceNumbersWithMachines }), [filters, userProfitCenterIds, viewSettings.onlyReferenceNumbersWithMachines])
    const { selectedPeriod, fairValueEvaluationPeriodsOptions, fetchFairValueEvaluationPeriods, changeSelectedPeriod, isLoading: isLoadingPeriods, noPeriods } = useFairValueEvaluationPeriods(userProfitCenterIds)
    const { fairValueEvaluations, loadNextPage, hasMore, noAssets, checkItem, isLoading, sort, setSort, linkGetBookValues, setEstimatedNetSalePrice } = useFairValueEvaluation({ bookingPeriod: selectedPeriod?.period, filters: filtersData })

    useEffect(() => {
        const element = scrollEl.current
        if (!element) return

        const handleScroll = debounce(() => {
            const current = element.scrollTop > 0
            setIsPageScrolled(prev => (prev !== current ? current : prev))
        }, 100)

        element.addEventListener("scroll", handleScroll)
        return () => {
            element.removeEventListener("scroll", handleScroll)
            handleScroll.cancel()
        }
    }, [])

    const { assetBookValues, referenceNumbersBookValues } = useBookValues(linkGetBookValues, fairValueEvaluations)
    const [expandedItems, setExpandedItems] = useState<Record<Guid, boolean>>({})
    const [checkLoading, setCheckLoading] = useState<Record<number, boolean>>({})
    const scrollEl = useRef<HTMLDivElement>(null)
    const [isPageScrolled, setIsPageScrolled] = useState(false)
    const freezeFirstColumn = true
    const freezeLastColumn = true
    const monthlyClosingPermission = hasPermission(UserPermission.MonthlyClosing)
    const [estimatedNetSalePriceFairValueData, setEstimatedNetSalePriceFairValueData] = useState<{
        link: IApiLink | null,
        referenceNumberSale: string,
        estimatedNetSalePrice?: AmountWithCurrency,
        estimatedSDC?: AmountWithCurrency,
        comment?: string,
        isNetSalePriceEditable?: boolean,
        automaticNetSalePrice?: AmountWithCurrency
    } | null>(null)

    const isFiltersApplied = useMemo(() => {
        //exclude referenceNumberSale filter from check
        return filters.some(filter => filter.category !== FilterCategory.referenceNumberSale)
    }, [filters])


    const checkedItemUserIds = useMemo<Guid[]>(() => {
        const idsSet = new Set<Guid>()

        fairValueEvaluations?.forEach(evaluation => {
            if (evaluation.check) {
                idsSet.add(evaluation.check.userId)
            }
            evaluation.entities.forEach(nestedEvaluation => {
                if (nestedEvaluation.check) {
                    idsSet.add(nestedEvaluation.check.userId)
                }
            })
        })
        return Array.from(idsSet)
    }, [fairValueEvaluations])

    const { getEmail } = useUserEmails({ userIds: checkedItemUserIds })

    useEffect(() => {
        fetchFairValueEvaluationPeriods({ initSelectedPeriod: true })
    }, [fetchFairValueEvaluationPeriods])

    const toggleExpandedItem = useCallback((id: Guid) => {
        setExpandedItems(prev => {
            return { ...prev, [id]: !prev[id] }
        })
    }, [])

    const header = (
        <Header
            title={t('fairValueEvaluation.title')}
            theme="light"
        />
    )

    const createCheckHandler = useCallback((item: FairValueEvaluationItem, topLevelId?: Guid, referenceNumberSale?: string) => async () => {
        if (!item.linkCheck || !referenceNumberSale) return
        setCheckLoading(prev => ({ ...prev, [topLevelId || item.assetId || '']: true }))
        try {
            await checkItem(item.linkCheck, referenceNumberSale)
        }
        catch (e) {
            toastMessages.showSystemError()
        }
        setCheckLoading(prev => ({ ...prev, [topLevelId || item.assetId || '']: false }))
    }, [checkItem])

    const toolbar = (
        <Toolbar theme='light'>
            <Flex className="mt-1">
                <FlexItem shrink={0} className={cn(noPeriods && !isLoadingPeriods && 'hidden', "pr-2")}>
                    <BookingPeriodsSelect
                        value={selectedPeriod}
                        options={fairValueEvaluationPeriodsOptions}
                        onChange={changeSelectedPeriod} />
                </FlexItem>
                {!noPeriods && <Filter
                    storage={FilterStorage.fairValueEvaluation}
                    onlyUserProfitCenters
                    filterCategories={[
                        FilterCategory.profitCenter,
                        FilterCategory.assetCategory,
                        FilterCategory.referenceNumberSale,
                    ]}
                />}
                <FlexItem>
                    <ViewSettingsFairValueEvaluation
                        viewSettings={viewSettings}
                        setViewSettings={setViewSettings}
                    />
                </FlexItem>
            </Flex>
        </Toolbar>
    )

    const tableColumns: TableColumn[] = useMemo(() => [
        {
            title: t('fairValueEvaluation.refNoSale'),
            minWidth: "19rem",
            maxWidth: "19rem",
            width: "19rem",
            bodyClassName: "border-top-hidden",
            sort: sort.sortBy === FairValueEvaluationSortBy.referenceNumberSale ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({
                sortBy: FairValueEvaluationSortBy.referenceNumberSale,
                sortDirection,
            }),
        },
        {
            title: t('common.lmid'),
            minWidth: "19rem",
            maxWidth: "19rem",
            width: "19rem"
        },
        {
            title: t('common.article'),
            minWidth: "24rem",
            maxWidth: "24rem",
            width: "24rem"
        },
        {
            title: t('asset.yoc'),
            minWidth: "10rem",
            maxWidth: "10rem",
            width: "10rem"
        },
        {
            title: t('valuation.acquisitionDate'),
            minWidth: "16rem",
            maxWidth: "16rem",
            width: "16rem"
        },
        {
            title: t('fairValueEvaluation.acquisitionCosts'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem",
            sort: sort.sortBy === FairValueEvaluationSortBy.acquisitionCosts ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({
                sortBy: FairValueEvaluationSortBy.acquisitionCosts,
                sortDirection,
            }),
        },
        {
            title: t('asset.operatingHours'),
            minWidth: "16rem",
            maxWidth: "16rem",
            width: "16rem"
        },
        {
            title: t('asset.condition'),
            minWidth: "12rem",
            maxWidth: "12rem",
            width: "12rem"
        },
        {
            title: t('fairValueEvaluation.calculatedNetSalesPrice'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.estimatedNetSalesPrice'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.overheadCosts'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.rentalFees'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.sdc'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.fairValue'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },

        {
            title: t('valuation.bookValue'),
            minWidth: "20rem",
            maxWidth: "20rem",
            width: "20rem"
        },
        {
            title: t('fairValueEvaluation.check'),
            minWidth: "27rem",
            maxWidth: "27rem",
            width: "27rem",
            sort: sort.sortBy === FairValueEvaluationSortBy.check ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({
                sortBy: FairValueEvaluationSortBy.check,
                sortDirection,
            }),
        },

    ], [setSort, sort.sortBy, sort.sortDirection, t])


    const createRow = useCallback(({ item, toggleExpanded, isLoadingItem, referenceNumberSale }: {
        item: FairValueEvaluationItem & {
            id?: Guid, referenceNumberSale?: string | null,
            estimate?: {
                netSalePrice: AmountWithCurrency | null
                sdc: AmountWithCurrency | null
                comment: string | null
                userId: Guid | null
                date: Dayjs | null
            } | null
            isSalePriceEstimatedNetSalePrice?: boolean
            referenceNumberSaleEstimatedNetSalePrice?: AmountWithCurrency | null
        }, toggleExpanded?: () => void, isLoadingItem?: boolean, referenceNumberSale: string | null
    }) => {

        const isTopLevel = !!toggleExpanded
        const EmptyCell = isTopLevel ? "" : <NoValue />

        const BookValue = resolveBookValue({ item, id: isTopLevel ? item.id ?? null : item.assetId, bookValues: isTopLevel ? referenceNumbersBookValues : assetBookValues, EmptyCell, isTopLevel })

        // For top-level: use referenceNumberSaleEstimatedNetSalePrice or estimate.netSalePrice
        // For asset entities: handle both possible formats of assetEstimatedNetSalePrice
        const estimatedNetSalePriceValue = isTopLevel
            ? (item.referenceNumberSaleEstimatedNetSalePrice || item.estimate?.netSalePrice)
            : (item.assetEstimatedNetSalePrice?.value ||
                (item.assetEstimatedNetSalePrice && 'amount' in item.assetEstimatedNetSalePrice
                    ? new AmountWithCurrency(item.assetEstimatedNetSalePrice as any)
                    : null))

        return [toggleExpanded && item.id ?
            <div className='fair-value-evaluation-first-top-row'>
                <SpoilerHeader
                    title={<Flex alignItems="flex-start">
                        <Text>{item.referenceNumberSale}</Text>
                        {item.isMissingDepreciationRate && <FlexItem className='ml-1'>
                            <Tooltip content={t('fairValueEvaluation.missingDepreciationRate')} trigger="hover" delayShow={400}>
                                <StatusLight type={StatusLightType.warning} />
                            </Tooltip>
                        </FlexItem>}
                    </Flex>}
                    expanded={expandedItems[item.id]}
                    onToggle={toggleExpanded}
                />
            </div>
            :
            <div />,

        <Flex>
            {item.lmid ? <Lmid lmid={item.lmid} assetId={item.assetId} /> : EmptyCell}
            {item.isMissingDepreciationRate && !isTopLevel && <FlexItem className='ml-1'>
                <Tooltip content={t('fairValueEvaluation.missingDepreciationRate')} trigger="hover" delayShow={400}>
                    <StatusLight type={StatusLightType.warning} />
                </Tooltip>
            </FlexItem>}
        </Flex>,
        item.article ?
            <ArticleDescription
                articleDescription={item.article.description}
                articleId={item.article.id}
            /> : EmptyCell,
        <Text>
            {item.constructionYear ?? EmptyCell}
        </Text>,
        <Text>
            {item.acquisitionDate ?
                item.acquisitionDate.format(t('config.dateLongFormat')) : EmptyCell}
        </Text>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
            <Text bold={isTopLevel}>
                {item.acquisitionCosts ? item.acquisitionCosts.print() : EmptyCell}
            </Text>
        </Flex>,
        <Text>
            {
                typeof item.operatingHours === 'number' ?
                    t('common.xHours', { count: item.operatingHours })
                    :
                    EmptyCell
            }
        </Text>,
        <Text>
            {
                typeof item.condition === 'number' ?
                    `${item.condition} %`
                    :
                    EmptyCell
            }
        </Text>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
            <Text bold={isTopLevel}>
                {item.calculatedNetSalePrice ? item.calculatedNetSalePrice.print() : EmptyCell}
            </Text>
        </Flex>,
        <Flex alignItems="center" justifyContent="space-between" className="w-100">
            <Flex alignItems="center">
                {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
                <Text bold={isTopLevel}>
                    {estimatedNetSalePriceValue ? estimatedNetSalePriceValue.print() : EmptyCell}
                </Text>
            </Flex>
            {
                !!isTopLevel &&
                <FlexItem shrink={0}>
                    {item.isSalePriceEstimatedNetSalePrice ?
                        <Tooltip content={t('fairValueEvaluation.salePrice')} trigger="hover" >
                            <IconAutoLimit />
                        </Tooltip>
                        :
                        (!!item.estimate?.netSalePrice &&
                            <Tooltip content={item.estimate?.comment || t('fairValueEvaluation.estimatedValue')} trigger="hover" >
                                <IconLimitComment />
                            </Tooltip>)
                    }
                </FlexItem>
            }

        </Flex>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
            <Text bold={isTopLevel}>
                {item.overheadCosts ? item.overheadCosts.print() : EmptyCell}
            </Text>
        </Flex>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
            <Text bold={isTopLevel}>
                {item.rentalFees ? item.rentalFees.print() : EmptyCell}
            </Text>
        </Flex>,
        <Flex alignItems="center" justifyContent="space-between" className="w-100">
            <Flex alignItems="center">
                {!toggleExpanded && <IconHierarchy height={24} width={24} className="mr-1" />}
                <Text bold={isTopLevel}>
                    {isTopLevel ?
                        (item.estimate?.sdc ? item.estimate.sdc.print() : item.sdc?.print()) || EmptyCell
                        :
                        item.sdc ? item.sdc.print() : EmptyCell
                    }
                </Text>
            </Flex>
            {
                !!isTopLevel && !!item.estimate?.sdc &&
                <FlexItem shrink={0}>
                    <Tooltip content={item.estimate?.comment || t('fairValueEvaluation.estimatedValue')} trigger="hover" >
                        <IconLimitComment />
                    </Tooltip>
                </FlexItem>
            }
        </Flex>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}
            <Text bold={isTopLevel}>
                {item.fairValue ? item.fairValue.print() : EmptyCell}
            </Text>
        </Flex>,
        <Flex>
            {!isTopLevel && <IconHierarchy height={24} width={24} className="mr-1" />}

            {BookValue}
        </Flex>,
        <Grid columns="1fr 3fr 1fr">
            {item?.check &&
                <StatusLight type={StatusLightType.success} />
            }
            {!item.isMissingDepreciationRate && !item.check &&
                <StatusLight type={StatusLightType.error} />
            }
            {
                !item?.check && item.isMissingDepreciationRate && <GridItem />
            }
            <GridItem justifySelf="flex-end" className='pr-2' >
                {!item.check && item.linkCheck && !(isTopLevel && isFiltersApplied) &&
                    <Button
                        label={t('fairValueEvaluation.check')}
                        icon={<IconCheck />}
                        theme="grey"
                        round
                        loading={isLoadingItem}
                        onClick={createCheckHandler(item, item.id, referenceNumberSale ?? undefined)}
                    />}
                {item.check &&
                    <Text className='color-grey-500'>
                        {t("fairValueEvaluation.checkedOnXByY",
                            { date: item.check.date?.format(t('config.dateLongTimeFormat')), email: getEmail(item.check.userId) })}
                    </Text>
                }
            </GridItem >
            <GridItem justifySelf="flex-end" alignSelf="center">
                {item.linkSetEstimatedNetSalePrice &&
                    <Button
                        theme='grey'
                        round
                        icon={<IconPencil />}
                        onClick={() => {
                            if (!item.linkSetEstimatedNetSalePrice || !item.referenceNumberSale) return
                            setEstimatedNetSalePriceFairValueData({
                                link: item.linkSetEstimatedNetSalePrice || null,
                                referenceNumberSale: item.referenceNumberSale,
                                estimatedNetSalePrice: item.estimate?.netSalePrice ?? undefined,
                                estimatedSDC: item.estimate?.sdc ?? undefined,
                                comment: item.estimate?.comment ?? undefined,
                                isNetSalePriceEditable: !!item.linkSetEstimatedNetSalePrice,
                                automaticNetSalePrice: item.referenceNumberSaleEstimatedNetSalePrice ?? undefined
                            })
                        }}
                    />}
            </GridItem>
        </Grid>

        ]
    }
        , [referenceNumbersBookValues, assetBookValues, expandedItems, t, isFiltersApplied, createCheckHandler, getEmail])

    const tableRows = useMemo(() => {
        const rows: TableRow[] = []
        fairValueEvaluations?.forEach(item => {
            const toggleExpanded = () => toggleExpandedItem(item.id)
            const row: TableRow = createRow({ item, toggleExpanded, isLoadingItem: checkLoading[item.id], referenceNumberSale: item.referenceNumberSale })
            rows.push(row)
            if (expandedItems[item.id]) {
                const subRows = item.entities.map((entity) => createRow({ item: entity, isLoadingItem: checkLoading[entity.assetId ?? ''], referenceNumberSale: item.referenceNumberSale }))
                rows.push(...subRows)
            }

        });
        return rows
    }, [checkLoading, createRow, expandedItems, fairValueEvaluations, toggleExpandedItem])

    return (
        <Page
            header={header}
            toolbar={toolbar}
            theme="light"
            padding="0"
            fullHeight>
            {(!!fairValueEvaluations?.length) &&
                <div className="h-100 overflow-hidden relative">
                    <div className="w-100 h-100 overflow-y-scroll px-5 pb-5 relative scroll-el" ref={scrollEl}>
                        <Container type="full">
                            <InfiniteScroll
                                pageStart={0}
                                loadMore={loadNextPage}
                                initialLoad={true}
                                hasMore={hasMore}
                                getScrollParent={() => scrollEl.current}
                                useWindow={false}>
                                <div className="fair-value-evaluation-top bg-white">
                                    <Flex alignItems="center" wrap="wrap" gap="0.8rem" className='mb-4 pt-4'>
                                        <Tag
                                            theme={TagTheme.bordered}
                                            label={t('filter.missingDepreciationRate')}
                                            onClick={() => addFilter({
                                                category: FilterCategory.missingDepreciationRate,
                                                value: true,
                                            })}
                                        />
                                    </Flex>

                                    <Table
                                        columns={tableColumns}
                                        rows={tableRows}
                                        useAdvancedScrolling={true}
                                        stickyHeader={true}
                                        isPageScrolled={isPageScrolled}
                                        freezeFirstColumn={freezeFirstColumn}
                                        freezeLastColumn={freezeLastColumn}
                                        theme="border-all"
                                        headerTheme="grey"
                                        testId="fair-value-evaluation-table"
                                    />
                                </div>
                            </InfiniteScroll>
                        </Container>
                        {(isLoading || hasMore) &&
                            <div className="py-3 px-5">
                                <Spinner />
                            </div>
                        }
                    </div>
                </div>}
            {
                (isLoadingPeriods || (isLoading && !fairValueEvaluations?.length)) && < Flex className='h-100' justifyContent="center" alignItems="center">
                    <Spinner />

                </Flex>
            }
            {
                noPeriods &&
                <Flex className="mt-8 mb-4" flow="column" alignItems="center">
                    <img src={noDocumentsImg} alt="" />
                    <Text preLine className="mt-4 text-center">
                        {t("fairValueEvaluation.noPeriods")}
                    </Text>
                    {monthlyClosingPermission && <Button
                        className="mt-4"
                        label={t('fairValueEvaluation.showMonthlyClosings')}
                        onClick={() => navigate(Routes.monthlyClosing.getPath())}
                    />}
                </Flex>
            }

            {
                !isLoadingPeriods && !noPeriods && noAssets &&
                <Flex className="mt-8 mb-4 color-grey-500" flow="column" alignItems="center">
                    <img src={noDocumentsImg} alt="" />
                    <Text preLine className="mt-4 text-center">
                        {t("fairValueEvaluation.noAssets")}
                    </Text>
                </Flex>

            }
            {
                estimatedNetSalePriceFairValueData && <EditEstimatedNetSalePriceModal
                    onSave={setEstimatedNetSalePrice}
                    data={estimatedNetSalePriceFairValueData}
                    onClose={() => setEstimatedNetSalePriceFairValueData(null)}
                />
            }
        </Page >

    )
}

export default FairValueEvaluationPage
